import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts';
import { Card } from '../common/Card';
import { ChartLineUp } from '@phosphor-icons/react';

/**
 * LiveWinProbabilityChart - Displays real-time win probability chart during live games
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6
 * 
 * @param {Object} props
 * @param {Array<{time: string, homeProb: number, awayProb: number}>} props.data - Probability history data
 * @param {string} props.homeTeam - Home team name
 * @param {string} props.awayTeam - Away team name
 * @param {number} props.currentHomeProb - Current home team win probability (0-1)
 * @param {number} props.currentAwayProb - Current away team win probability (0-1)
 * @param {number} props.probChange - Probability change since last update
 */
export function LiveWinProbabilityChart({
  data = [],
  homeTeam = '',
  awayTeam = '',
  currentHomeProb = 0.5,
  currentAwayProb = 0.5,
  probChange = 0,
}) {
  const { t } = useTranslation();

  // Transform data for chart display (convert to percentages)
  const chartData = useMemo(() => {
    return data.map((point) => ({
      time: point.time,
      homeProb: (point.homeProb ?? 0.5) * 100,
      awayProb: (point.awayProb ?? 0.5) * 100,
    }));
  }, [data]);

  // Format probability change indicator (Requirement 2.6)
  const formatProbChange = (change) => {
    if (change === 0 || change === undefined || change === null) return '→0.0%';
    const sign = change > 0 ? '↑' : '↓';
    return `${sign}${Math.abs(change * 100).toFixed(1)}%`;
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-surface border border-glass-border rounded-lg p-3 shadow-lg">
          <p className="text-slate-400 text-xs mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p
              key={index}
              className="text-sm"
              style={{ color: entry.color }}
            >
              {entry.name}: {entry.value?.toFixed(1)}%
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Calculate stats for display
  const stats = useMemo(() => {
    if (chartData.length === 0) {
      return { homeHigh: 50, homeLow: 50, awayHigh: 50, awayLow: 50 };
    }
    const homeProbs = chartData.map((d) => d.homeProb);
    const awayProbs = chartData.map((d) => d.awayProb);
    return {
      homeHigh: Math.max(...homeProbs).toFixed(1),
      homeLow: Math.min(...homeProbs).toFixed(1),
      awayHigh: Math.max(...awayProbs).toFixed(1),
      awayLow: Math.min(...awayProbs).toFixed(1),
    };
  }, [chartData]);

  return (
    <Card className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-accent flex items-center gap-2">
          <ChartLineUp size={20} weight="duotone" /> {t('live.winProbability')}
        </h3>
        {/* Current probability change indicator (Requirement 2.6) */}
        <div className="flex items-center gap-3">
          <span
            className={`text-sm font-medium ${
              probChange >= 0 ? 'text-positive' : 'text-negative'
            }`}
          >
            {formatProbChange(probChange)}
          </span>
        </div>
      </div>

      {/* Current probabilities display */}
      <div className="flex justify-between items-center mb-4 px-4">
        <div className="text-center">
          <div className="text-xs text-gray-400 mb-1">{awayTeam}</div>
          <div className="text-xl font-bold text-accent">
            {(currentAwayProb * 100).toFixed(1)}%
          </div>
        </div>
        <div className="text-gray-500 text-sm">VS</div>
        <div className="text-center">
          <div className="text-xs text-gray-400 mb-1">{homeTeam}</div>
          <div className="text-xl font-bold text-accent-warm">
            {(currentHomeProb * 100).toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Chart (Requirements 2.1, 2.2, 2.3, 2.4, 2.5) */}
      <div className="h-64">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              {/* X-Axis with time labels (Requirement 2.4) */}
              <XAxis
                dataKey="time"
                tick={{ fill: '#9aa6b2', fontSize: 10 }}
                interval="preserveStartEnd"
                axisLine={{ stroke: '#3b4758' }}
                tickLine={{ stroke: '#3b4758' }}
              />
              {/* Y-Axis with percentage labels (Requirement 2.5) */}
              <YAxis
                domain={[0, 100]}
                tick={{ fill: '#9aa6b2', fontSize: 10 }}
                tickFormatter={(v) => `${v}%`}
                width={45}
                axisLine={{ stroke: '#3b4758' }}
                tickLine={{ stroke: '#3b4758' }}
              />
              {/* 50% reference line */}
              <ReferenceLine
                y={50}
                stroke="#5c6674"
                strokeDasharray="3 3"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: 10 }}
                formatter={(value) => (
                  <span style={{ color: '#9aa6b2', fontSize: 12 }}>
                    {value}
                  </span>
                )}
              />
              {/* Away team line - Blue (Requirement 2.2) */}
              <Line
                type="monotone"
                dataKey="awayProb"
                stroke="#6f8db0"
                strokeWidth={2}
                dot={false}
                name={awayTeam || t('match.away')}
                activeDot={{ r: 4, fill: '#6f8db0' }}
              />
              {/* Home team line - Red (Requirement 2.2) */}
              <Line
                type="monotone"
                dataKey="homeProb"
                stroke="#c49a5c"
                strokeWidth={2}
                dot={false}
                name={homeTeam || t('match.home')}
                activeDot={{ r: 4, fill: '#c49a5c' }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500">
            {t('common.noData')}
          </div>
        )}
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-4 gap-3 mt-4 pt-4 border-t border-glass-border">
        <div className="text-center">
          <div className="text-xs text-gray-400">{awayTeam} {t('detail.highest')}</div>
          <div className="font-medium text-accent">{stats.awayHigh}%</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-400">{awayTeam} {t('detail.lowest')}</div>
          <div className="font-medium text-accent">{stats.awayLow}%</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-400">{homeTeam} {t('detail.highest')}</div>
          <div className="font-medium text-accent-warm">{stats.homeHigh}%</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-400">{homeTeam} {t('detail.lowest')}</div>
          <div className="font-medium text-accent-warm">{stats.homeLow}%</div>
        </div>
      </div>
    </Card>
  );
}
